import type { Priority } from "@app/core";
import type { CSSProperties } from "react";
import { DEFAULT_BLOCK_TINT, EVENT_DEFAULT_HEX, mixColors, pastel, PRIORITY_FLAG_HEX, type BlockTint } from "./task-colors.js";

/** Color inputs for a chip/block. The custom/topic color fills the WHOLE block;
 *  when BOTH a custom color and a topic/calendar color exist they blend across
 *  the block. Priority only ever paints a soft blob from the top. */
interface WashTask {
  priority: Priority;
  custom_color: string | null;
  is_event?: boolean;
}

export interface WashResult {
  /** CSS custom properties (+ text `color`) consumed by the `.task-wash` class
   *  (see globals.css): `--c-fill` (solid fallback) + `--c-bg-image` (the
   *  composed background layers). Empty when `washed` is false. */
  vars: CSSProperties;
  /** True ⇒ apply the `.task-wash` class; false ⇒ fall back to the tone class. */
  washed: boolean;
}

const isHex = (c: string) => /^#?[0-9a-f]{6}$/i.test(c.trim());

/** Soften a color toward white by `amount` ∈ [0,1]. Uses `pastel()` for hex and
 *  oklch `color-mix` otherwise (the priority paper palette is oklch). */
const soften = (color: string, amount: number) =>
  isHex(color) ? pastel(color, amount) : `color-mix(in oklch, ${color}, white ${Math.round(Math.max(0, Math.min(1, amount)) * 100)}%)`;

/** Whether the ACTUAL displayed fill (the already-softened color) reads as
 *  light. The threshold leans toward "dark" so darker fills get light text.
 *  `fill` is hex when `primary` was hex (the common case); for an oklch
 *  `primary` we estimate lightness after the white-mix. */
function fillIsLight(fill: string, primary: string, amount: number): boolean {
  const h = /^#?([0-9a-f]{6})$/i.exec(fill.trim());
  if (h) {
    const n = parseInt(h[1]!, 16);
    const lum = 0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff);
    return lum > 152;
  }
  const o = /oklch\(\s*([\d.]+%?)/i.exec(primary);
  if (o) {
    let L = parseFloat(o[1]!);
    if (o[1]!.includes("%")) L /= 100;
    return L + (1 - L) * amount > 0.62; // lightness after white-mix
  }
  return false;
}

// NOTE on blending: semi-transparent layers of two unrelated hues composite in
// sRGB, and complementary-ish pairs meet in the middle at muddy GRAY. Wherever
// two of OUR colors must transition, the intermediate stops are pre-mixed in
// oklch (`color-mix(in oklch, A, B)`) so the falloff travels through vivid
// in-between hues instead of desaturating.

/**
 * Block background, composed as stacked CSS background layers (`--c-bg-image`)
 * over a dominant `--c-fill` base color (so the block reads as ONE clear color):
 *  - **Dominant fill**: the custom color → topic/calendar context → event
 *    default, covering the whole block (a slight diagonal sheen when `gradient`,
 *    else flat).
 *  - **Topic + custom** (both set): the custom color dominates; the topic color
 *    bleeds in as a soft watercolor accent from the LEFT.
 *  - **Priority** (if set): a soft watercolor accent bleeding from the TOP — it
 *    never recolors the whole block.
 * `washed: false` ⇒ the task has no color of its own (a bare priority included)
 * → caller keeps the tone class.
 *
 * `tint` (from `useBlockStyle`) softens the colors — tasks lighter, events more
 * saturated. Pass `contextColor` from `useTaskContextColor`.
 */
export function washStyle(
  task: WashTask,
  contextColor?: string | string[] | null,
  tint: BlockTint = DEFAULT_BLOCK_TINT,
  gradient = true,
  /** Continuous matrix diagonal color (green→red) for a matrix-PLACED task in
   *  scope (resolve via `useMatrixPriorityColor`). When set it acts as the
   *  task's priority color: it FILLS a bare block (no custom/topic/event color)
   *  and otherwise paints the top-right accent — so positions within one
   *  discrete band (e.g. med/high vs med/med) still read as distinct colors.
   *  Omitted/null ⇒ the discrete flag hue, unchanged. */
  priorityColor?: string | null,
): WashResult {
  const custom = task.custom_color || null;
  const contextColors = Array.isArray(contextColor)
    ? contextColor.filter(Boolean)
    : contextColor ? [contextColor] : [];
  const context = mixColors(contextColors);
  // A distinct dominant fill (custom → topic/calendar → event slate). The
  // continuous priority color only becomes the fill when nothing else colors
  // the block (mirrors the discrete tone-class fallback), so topic/custom
  // colors keep dominating and the matrix color paints the accent instead.
  const dominant = custom || context || (task.is_event ? EVENT_DEFAULT_HEX : null);
  const primary = dominant || priorityColor || null;
  if (!primary) return { vars: {}, washed: false };

  const amount = task.is_event ? tint.event : tint.task;
  const fill = soften(primary, amount);
  const softenedContexts = contextColors.map((color) => soften(color, amount));

  // Accents are painted ABOVE the dominant fill (earlier layers sit on top).
  const layers: string[] = [];
  // Continuous matrix color if supplied, else the discrete flag hue. Only
  // painted OVER a distinct dominant fill — when the priority color IS the fill
  // (a bare matrix block), a second same-color blob would just muddy it.
  const accentColor = priorityColor ?? (task.priority !== "none" ? PRIORITY_FLAG_HEX[task.priority] : null);
  if (dominant && accentColor) {
    // Priority prominently colors the TOP-RIGHT corner (where the flag sits) in
    // the vivid priority color — barely softened so it reads against any fill —
    // then fades VERY gradually across the block (spans well past the corner).
    // Each stop is pre-mixed toward the fill in oklch (see note above) so the
    // alpha tail never grays out against an unrelated fill hue.
    const p = pastel(accentColor, 0.05);
    const pm = (t: number) => (t === 0 ? p : `color-mix(in oklch, ${fill} ${t}%, ${p})`);
    layers.push(
      `radial-gradient(135% 165% at 100% 0%, ` +
      `color-mix(in oklch, ${pm(0)}, transparent 2%) 0%, ` +
      `color-mix(in oklch, ${pm(14)}, transparent 16%) 14%, ` +
      `color-mix(in oklch, ${pm(28)}, transparent 36%) 25%, ` +
      `color-mix(in oklch, ${pm(45)}, transparent 56%) 40%, ` +
      `color-mix(in oklch, ${pm(62)}, transparent 76%) 60%, ` +
      `color-mix(in oklch, ${pm(74)}, transparent 90%) 75%, ` +
      `transparent 100%)`,
    );
  }
  if (custom && context) {
    // Topic color bleeds from the LEFT, blended into the dominant fill with
    // OPAQUE oklch-interpolated stops — an alpha-faded overlay composited the
    // two hues in sRGB and the crossover read as dirty gray.
    const ctx = soften(context, amount);
    const mix = (t: number) => `color-mix(in oklch, ${fill} ${t}%, ${ctx})`;
    const tail = gradient
      ? `${fill} 62%, color-mix(in oklch, ${fill}, black 6%) 100%`
      : `${fill} 62%`;
    // The 0% stop is 75% fill so the left edge stays close to the dominant
    // color — a hard ctx strip at 0% read as a "wrong color" artifact.
    layers.push(
      `linear-gradient(100deg, ${mix(15)} 0%, ${mix(28)} 8%, ${mix(50)} 18%, ${mix(70)} 30%, ${mix(88)} 44%, ${tail})`,
    );
  } else if (softenedContexts.length > 1) {
    const last = softenedContexts.length - 1;
    layers.push(
      `linear-gradient(100deg, ${softenedContexts.map((color, i) =>
        `${color} ${Math.round((i / last) * 100)}%`,
      ).join(", ")})`,
    );
  } else if (gradient) {
    // Dominant fill layer (sheen) sits at the bottom of the stack. When flat,
    // it's omitted and `--c-fill` (background-color) shows beneath any accents.
    layers.push(`linear-gradient(160deg, color-mix(in oklch, ${fill}, white 8%) 0%, ${fill} 60%, color-mix(in oklch, ${fill}, black 6%) 100%)`);
  }

  const light = fillIsLight(fill, primary, amount);
  const vars: CSSProperties = {
    ["--c-fill" as string]: fill,
    ["--c-bg-image" as string]: layers.length ? layers.join(", ") : "none",
    // A same-family ink for small marks on the block (series icon): a darker
    // shade of the displayed fill on light fills, a lighter one on dark fills —
    // always visible, never a foreign color.
    ["--c-ink" as string]: light
      ? `color-mix(in oklch, ${fill}, black 45%)`
      : `color-mix(in oklch, ${fill}, white 45%)`,
    // Auto-contrast the text against the actual displayed fill (always legible).
    color: light ? "#1f2937" : "#f8fafc",
    // Tonal border: same hue as the fill, slightly shifted so it reads as a
    // subtle edge rather than the "wrong color" that currentColor (text color)
    // produces against an unrelated fill hue.
    borderColor: light
      ? `color-mix(in oklch, ${fill}, black 22%)`
      : `color-mix(in oklch, ${fill}, white 22%)`,
  };
  return { vars, washed: true };
}
